/**
 * Drill context — one DrillExplorer for the whole app, opened from any card via
 * useDrill().open(req). Mounted once in AppLayout so every page shares it and no
 * page has to manage its own modal state.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { DrillExplorer, type DrillRequest } from "../components/DrillExplorer.js";
import { RatioDrill } from "../components/RatioDrill.js";
import { LtvDrill } from "../components/LtvDrill.js";
import { NodeDrill } from "../components/NodeDrill.js";
import { ROUTE_LABELS } from "./card-catalog.js";

interface DrillCtx {
  open: (req: DrillRequest) => void;
}

const Ctx = createContext<DrillCtx | null>(null);

/** Friendly name of the page a drill was opened from (for the breadcrumb). */
function originLabel(path: string): string {
  return ROUTE_LABELS[path] ?? "Exec";
}

export function DrillProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [req, setReq] = useState<DrillRequest | null>(null);
  const [origin, setOrigin] = useState("/dashboard");
  const [seq, setSeq] = useState(0);
  const openRef = useRef(false);

  const open = useCallback((r: DrillRequest) => {
    if (typeof window !== "undefined") {
      setOrigin(window.location.pathname);
      // The drill is a full-screen OVERLAY, not a route. Push ONE history entry
      // so the browser BACK button closes it (back to the page it was opened
      // from) instead of navigating the app away. Replace (not stack) if a drill
      // is somehow already open, so there's never an orphan entry (Codex).
      if (openRef.current) window.history.replaceState({ ddDrill: true }, "");
      else window.history.pushState({ ddDrill: true }, "");
      openRef.current = true;
    }
    setReq(r);
    // Remount per open: DrillExplorer seeds its state from req in useState
    // initializers, which DON'T re-run when the same component instance gets
    // a new req — opening drill B while A was open kept A's view/filters.
    setSeq((s) => s + 1);
  }, []);

  // One persistent listener: close only when we popped OFF our ddDrill entry, so
  // real React Router navigations pass through untouched (Codex). X / Escape /
  // breadcrumb route through close() → history.back() → here, so all four paths
  // behave the same (founder 2026-06-16: "X or back should go back to exec").
  useEffect(() => {
    const onPop = (e: PopStateEvent): void => {
      if (openRef.current && !(e.state as { ddDrill?: boolean } | null)?.ddDrill) {
        openRef.current = false;
        setReq(null);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const close = useCallback(() => {
    if (typeof window !== "undefined" && openRef.current && (window.history.state as { ddDrill?: boolean } | null)?.ddDrill) {
      window.history.back(); // → popstate → clears the drill; no dangling entry
    } else {
      openRef.current = false;
      setReq(null);
    }
  }, []);

  return (
    <Ctx.Provider value={{ open }}>
      {children}
      {req && (req.node
        ? <NodeDrill key={seq} req={req} onClose={close} originLabel={originLabel(origin)} />
        : req.ltv
        ? <LtvDrill key={seq} req={req} onClose={close} originLabel={originLabel(origin)} />
        : req.ratio
        ? <RatioDrill key={seq} req={req} onClose={close} originLabel={originLabel(origin)} />
        : <DrillExplorer key={seq} req={req} onClose={close} originLabel={originLabel(origin)} />)}
    </Ctx.Provider>
  );
}

/**
 * Returns { open }. Safe to call outside a provider (open is a no-op) so a card
 * never crashes if it's rendered somewhere without the provider mounted.
 */
export function useDrill(): DrillCtx {
  return useContext(Ctx) ?? { open: () => {} };
}

export type { DrillRequest };
